import { CommonModule, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, signal } from '@angular/core';

@Component({
  selector: 'table-pagination',
  imports: [NgIf, NgFor, CommonModule],
  templateUrl: './pagination.component.html',
})
export class PaginationComponent implements OnInit, OnChanges {
  @Input() totalRecords = 0;
  @Input() recordsPerPage = 10;
  @Input() totalElements = 0;
  @Input() currentPageValue = 1;  // Nuevo input para recibir la página actual
  @Output() pageChange = new EventEmitter<number>();
  @Output() recordsPerPageChange = new EventEmitter<number>();

  currentPage = signal(1);  // Signal interno para la UI

  ngOnInit(): void {
    this.currentPage.set(this.currentPageValue);
    this.calculatePagination();
  }

  ngOnChanges(): void {
    // Actualizar la página actual cuando cambia desde el exterior
    if (this.currentPageValue !== this.currentPage()) {
      this.currentPage.set(this.currentPageValue);
    }
    this.calculatePagination();
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.recordsPerPage);
  }

  get visiblePages(): number[] {
    const pages = [];
    const current = this.currentPage();
    const total = this.totalPages;
    
    if (total <= 5) {
      // Si hay 5 o menos páginas, mostrar todas
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Siempre mostrar la primera página
      pages.push(1);
      
      // Mostrar páginas alrededor de la actual
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      
      // Agregar elipsis después de la primera página si es necesario
      if (start > 2) pages.push(-1); // -1 representa "..."
      
      // Agregar páginas del medio
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      // Agregar elipsis antes de la última página si es necesario
      if (end < total - 1) pages.push(-2); // -2 representa otro "..."
      
      // Siempre mostrar la última página
      if (total > 1) pages.push(total);
    }
    
    return pages;
  }

  get showEllipsis(): boolean {
    return this.totalPages > 5;
  }

  calculatePagination(): void {
    // Asegurar que la página actual es válida
    if (this.currentPage() > this.totalPages && this.totalPages > 0) {
      this.currentPage.set(1);
      this.pageChange.emit(1);
    }
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.set(this.currentPage() - 1);
      this.pageChange.emit(this.currentPage());
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.set(this.currentPage() + 1);
      this.pageChange.emit(this.currentPage());
    }
  }

  goToPage(page: number): void {
    if (page > 0) { // Ignorar clicks en elipsis (-1, -2)
      this.currentPage.set(page);
      this.pageChange.emit(this.currentPage());
    }
  }
}